import {
  ScheduleServiceClient,
  ListScheduleItemsRequest,
  ListScheduleItemsResponse,
  NewScheduleRequest,
  NewScheduleResponse,
  ReadScheduleRequest,
  ReadScheduleResponse,
  AddScheduleItemRequest,
  AddScheduleItemResponse,
  ActivateScheduleItemRequest,
  ActivateScheduleItemResponse,
  DeactivateScheduleItemRequest,
  DeactivateScheduleItemResponse,
  UpdateScheduleItemRequest,
  UpdateScheduleItemResponse,
  RemoveScheduleItemRequest,
  RemoveScheduleItemResponse,
  ExportCSVRequest,
  ExportCSVResponse,
  BulkUpdateScheduleItemRequest,
  BulkUpdateScheduleItemResponse,
  BulkAddScheduleItemResponse,
  BulkAddScheduleItemRequest,
  ClearScheduleRequest,
  ClearScheduleResponse,
} from '@policyfly/protobuf/pfschedule'

import { devtools } from '@/plugins/devtools/api'

import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'

export interface ScheduleApiEndpoints {
  /**
   * See {@link ScheduleServiceClient.newSchedule newSchedule}.
   */
  create: (request: NewScheduleRequest) => Promise<NewScheduleResponse>
  /**
   * See {@link ScheduleServiceClient.readSchedule readSchedule}.
   */
  read: (request: ReadScheduleRequest) => Promise<ReadScheduleResponse>
  /**
   * See {@link ScheduleServiceClient.ListScheduleItemsRequest listScheduleItems}.
   */
  list: (request: ListScheduleItemsRequest) => Promise<ListScheduleItemsResponse>
  /**
   * See {@link ScheduleServiceClient.addScheduleItem addScheduleItem}.
   */
  addItem: (request: AddScheduleItemRequest) => Promise<AddScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.bulkAddScheduleItem bulkAddScheduleItem}.
   */
  bulkAdd: (request: BulkAddScheduleItemRequest) => Promise<BulkAddScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.activateScheduleItem activateScheduleItem}.
   */
  activateItem: (request: ActivateScheduleItemRequest) => Promise<ActivateScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.deactivateScheduleItem deactivateScheduleItem}.
   */
  deactivateItem: (request: DeactivateScheduleItemRequest) => Promise<DeactivateScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.removeScheduleItem removeScheduleItem}.
   */
  removeItem: (request: RemoveScheduleItemRequest) => Promise<RemoveScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.updateScheduleItem updateScheduleItem}.
   */
  updateItem: (request: UpdateScheduleItemRequest, abortController?: AbortController) => Promise<UpdateScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.bulkUpdateScheduleItems bulkUpdateScheduleItems}.
   */
  bulkUpdate: (request: BulkUpdateScheduleItemRequest) => Promise<BulkUpdateScheduleItemResponse>
  /**
   * See {@link ScheduleServiceClient.exportCSV exportCSV}.
   */
  exportCSV: (request: ExportCSVRequest) => Promise<ExportCSVResponse>
  /**
   * See {@link ScheduleServiceClient.clearScheduleRequest clearScheduleRequest}.
   */
  clearAll: (request: ClearScheduleRequest) => Promise<ClearScheduleResponse>
}

/**
 * Creates endpoints related to the Schedule service workflow.
 */
export const createScheduleEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<ScheduleApiEndpoints> => {
  const scheduleServiceClient = new ScheduleServiceClient(params.transport)

  const create: ScheduleApiEndpoints['create'] = async function (request) {
    const { response } = await scheduleServiceClient.newSchedule(request)
    devtools.logGrpc({
      description: 'New Schedule',
      messages: [
        { type: NewScheduleRequest, key: 'request', message: request },
        { type: NewScheduleResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const read: ScheduleApiEndpoints['read'] = async function (request) {
    const { response } = await scheduleServiceClient.readSchedule(request)
    devtools.logGrpc({
      description: 'Read Schedule',
      messages: [
        { type: ReadScheduleRequest, key: 'request', message: request },
        { type: ReadScheduleResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const list: ScheduleApiEndpoints['list'] = async function (request) {
    const { response } = await scheduleServiceClient.listScheduleItems(request)
    devtools.logGrpc({
      description: 'List Schedule Items',
      messages: [
        { type: ListScheduleItemsRequest, key: 'request', message: request },
        { type: ListScheduleItemsResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const addItem: ScheduleApiEndpoints['addItem'] = async function (request) {
    const { response } = await scheduleServiceClient.addScheduleItem(request)
    devtools.logGrpc({
      description: 'Add Schedule Item',
      messages: [
        { type: AddScheduleItemRequest, key: 'request', message: request },
        { type: AddScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const bulkAdd: ScheduleApiEndpoints['bulkAdd'] = async function (request) {
    const { response } = await scheduleServiceClient.bulkAddScheduleItem(request)
    devtools.logGrpc({
      description: 'Bulk Add Schedule Items',
      messages: [
        { type: BulkAddScheduleItemRequest, key: 'request', message: request },
        { type: BulkAddScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const activateItem: ScheduleApiEndpoints['activateItem'] = async function (request) {
    const { response } = await scheduleServiceClient.activateScheduleItem(request)
    devtools.logGrpc({
      description: 'Activate Schedule Item',
      messages: [
        { type: ActivateScheduleItemRequest, key: 'request', message: request },
        { type: ActivateScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const deactivateItem: ScheduleApiEndpoints['deactivateItem'] = async function (request) {
    const { response } = await scheduleServiceClient.deactivateScheduleItem(request)
    devtools.logGrpc({
      description: 'Deactivate Schedule Item',
      messages: [
        { type: DeactivateScheduleItemRequest, key: 'request', message: request },
        { type: DeactivateScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const updateItem: ScheduleApiEndpoints['updateItem'] = async function (request, abortController) {
    const { response } = await scheduleServiceClient.updateScheduleItem(request, { abort: abortController?.signal })
    devtools.logGrpc({
      description: 'Update Schedule Item',
      messages: [
        { type: UpdateScheduleItemRequest, key: 'request', message: request },
        { type: UpdateScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const bulkUpdate: ScheduleApiEndpoints['bulkUpdate'] = async function (request) {
    const { response } = await scheduleServiceClient.bulkUpdateScheduleItem(request)
    devtools.logGrpc({
      description: 'Bulk Update Schedule Items',
      messages: [
        { type: BulkUpdateScheduleItemRequest, key: 'request', message: request },
        { type: BulkUpdateScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const removeItem: ScheduleApiEndpoints['removeItem'] = async function (request) {
    const { response } = await scheduleServiceClient.removeScheduleItem(request)
    devtools.logGrpc({
      description: 'Remove Schedule Item',
      messages: [
        { type: RemoveScheduleItemRequest, key: 'request', message: request },
        { type: RemoveScheduleItemResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const exportCSV: ScheduleApiEndpoints['exportCSV'] = async function (request) {
    const { response } = await scheduleServiceClient.exportCSV(request)
    devtools.logGrpc({
      description: 'Export Schedule CSV',
      messages: [
        { type: ExportCSVRequest, key: 'request', message: request },
        { type: ExportCSVResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const clearAll: ScheduleApiEndpoints['clearAll'] = async function (request) {
    const { response } = await scheduleServiceClient.clearSchedule(request)
    devtools.logGrpc({
      description: 'Clear Schedule',
      messages: [
        { type: ClearScheduleRequest, key: 'request', message: request },
        { type: ClearScheduleResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const endpoints: ScheduleApiEndpoints = {
    create,
    read,
    list,
    addItem,
    bulkAdd,
    activateItem,
    deactivateItem,
    updateItem,
    bulkUpdate,
    removeItem,
    exportCSV,
    clearAll,
  }
  return {
    django: endpoints,
    grpc: endpoints,
  }
}
